/**
 * JSON-LD @graph for the public `/articles` index.
 *
 * Emits CollectionPage + ItemList + BreadcrumbList as a single @graph. The
 * ItemList enumerates every article shown on the page (both the house "Guides"
 * shelf and the coach-authored feed), each pointing at its `/articles/{slug}`
 * detail page. Individual article pages carry their own Article schema — this
 * describes the collection, not the items' bodies.
 *
 * Aliases: articles index jsonld, collectionpage schema, itemlist schema
 */

import {
  BRAND_ORGANIZATION_ID,
  BRAND_URL,
  BRAND_WEBSITE_ID,
} from "~/lib/shared/brand";

const PAGE_URL = `${BRAND_URL}/articles`;

export interface ArticlesIndexJsonLdItem {
  readonly slug: string;
  readonly title: string;
}

export function buildArticlesIndexJsonLd(
  items: readonly ArticlesIndexJsonLdItem[],
): { "@context": string; "@graph": Array<Record<string, unknown>> } {
  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "CollectionPage",
      "@id": `${PAGE_URL}#collection`,
      url: PAGE_URL,
      name: "Articles | Dream Coach Match",
      description:
        "Practical, expert-written articles from credentialed coaches on life's biggest transitions.",
      isPartOf: { "@id": BRAND_WEBSITE_ID },
      publisher: { "@id": BRAND_ORGANIZATION_ID },
      mainEntity: { "@id": `${PAGE_URL}#itemlist` },
      inLanguage: "en-US",
    },
    {
      "@type": "ItemList",
      "@id": `${PAGE_URL}#itemlist`,
      itemListOrder: "https://schema.org/ItemListUnordered",
      numberOfItems: items.length,
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${BRAND_URL}/articles/${item.slug}`,
        name: item.title,
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${PAGE_URL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BRAND_URL },
        { "@type": "ListItem", position: 2, name: "Articles", item: PAGE_URL },
      ],
    },
  ];

  return { "@context": "https://schema.org", "@graph": graph };
}
